import { createSteeringURL } from '@/api'
import { steeringAuthInit } from '@/stores/auth'
import { useMediaStore } from '@/stores/media'
import { MediaSource as _MediaSource, MediaSourceCreateData, MediaSourceUpdateData } from '@/types'
import { aggregateWithIdsParameter, APIReorder, useStoreHelpers } from '@/util/api'
import {
  APICreate,
  APIEvents,
  APIListPaginated,
  APIRemove,
  APIRetrieve,
  APIUpdate,
  createExtendableAPI,
  ExtendableAPI,
} from '@rokoli/bnb/drf'
import { defineStore } from 'pinia'

type MediaSourceEvent = APIEvents<_MediaSource>
type InternalMediaSourceType = 'file' | 'local-file' | 'm3u' | 'line' | 'stream' | 'unknown'

export function determineInternalMediaSourceType(
  mediaSource: _MediaSource,
): InternalMediaSourceType {
  if (mediaSource.type === 'aura-structuredaudio') return 'file'
  if (mediaSource.type === 'm3u') return 'm3u'
  if (mediaSource.type === 'audio' && /^line:/.test(mediaSource.source)) return 'line'
  if (mediaSource.type === 'audio' && /^https?:/.test(mediaSource.source)) return 'stream'
  if (mediaSource.type === 'audio' && /^file:/.test(mediaSource.source)) return 'local-file'
  return 'unknown'
}

export function APIMediaSourceReorder(api: ExtendableAPI<_MediaSource>) {
  const ops = APIReorder(api)
  const _reorder = ops.reorder

  async function refreshMediaFromSourceIds(sourceIds: Iterable<_MediaSource['id']>) {
    const mediaStore = useMediaStore()
    const mediaSourceStore = useMediaSourceStore()
    const sources = await mediaSourceStore.retrieveMultiple(Array.from(sourceIds))
    const mediaIds = new Set(
      sources.map((result) =>
        result.status === 'fulfilled' ? (result.value as _MediaSource).mediaId : null,
      ),
    )
    await mediaStore.retrieveMultiple(
      Array.from(mediaIds).filter((v) => v !== null),
      { useCached: false },
    )
  }

  ops.reorder = async (orderings, options) => {
    await _reorder(orderings, options)
    // Media sources are often used directly from the media object,
    // and we need to refresh the media if we change the source order.
    await refreshMediaFromSourceIds(orderings.map((o) => o.id))
  }
  return ops
}

export const useMediaSourceStore = defineStore('mediaSources', () => {
  const endpoint = createSteeringURL.prefix('media-sources')
  const { api, base } = createExtendableAPI<_MediaSource>(endpoint, steeringAuthInit)
  const listOperations = APIListPaginated(api)
  const retrieveOperations = APIRetrieve(api, {
    aggregate: aggregateWithIdsParameter(listOperations.listIsolated),
  })

  function refreshMediaFromEvent(event: MediaSourceEvent['set'] | MediaSourceEvent['remove']) {
    const mediaStore = useMediaStore()
    const { id, object } = event.detail

    const mediaId = object
      ? object.mediaId
      : (mediaStore.items.find(
          (media) => media.entries.length > 0 && media.entries.some((source) => source.id === id),
        )?.id ?? null)

    if (mediaId) {
      void mediaStore.retrieve(mediaId, { useCached: false })
    }
  }

  api.events.addEventListener('set', refreshMediaFromEvent)
  api.events.addEventListener('remove', refreshMediaFromEvent)

  return {
    ...base,
    ...listOperations,
    ...retrieveOperations,
    ...APICreate<_MediaSource, MediaSourceCreateData>(api),
    ...APIUpdate<_MediaSource, MediaSourceUpdateData, MediaSourceUpdateData>(api),
    ...APIMediaSourceReorder(api),
    ...APIRemove(api),
    ...useStoreHelpers({ ...base, ...listOperations, ...retrieveOperations }),
  }
})
